import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faList,
  faClock,
  faFileAlt,
  faTruckLoading,
  faTruckMoving,
} from "@fortawesome/free-solid-svg-icons";

const MiniDashboardTraffic = ({ trafficData }) => {
  const uniqueReferencesCount = new Set(
    trafficData.map((item) => item.header.reference)
  ).size;
  const uniqueCustomersCount = new Set(
    trafficData.map((item) => item.header.customer_rfc)
  ).size;

  // Calculate Attention Time with validation
  const attentionTimes = trafficData
    .map((item) => {
      const dateInit = new Date(item.header.detail.dates_data.date_init);
      const dateCrossing = new Date(
        item.header.detail.dates_data.date_crossing
      );

      if (isNaN(dateInit.getTime()) || isNaN(dateCrossing.getTime())) {
        return null; // Discard entry if either date is invalid
      }

      return (dateCrossing - dateInit) / (1000 * 60 * 60); // Convert to hours
    })
    .filter((time) => time !== null); // Filter out invalid times

  const averageAttentionTime =
    attentionTimes.length > 0
      ? (
          attentionTimes.reduce((acc, curr) => acc + curr, 0) /
          attentionTimes.length
        ).toFixed(2)
      : "N/A";

  const maxAttentionTime =
    attentionTimes.length > 0 ? Math.max(...attentionTimes).toFixed(2) : "N/A";

  const minAttentionTime =
    attentionTimes.length > 0 ? Math.min(...attentionTimes).toFixed(2) : "N/A";

  // Calculate Doc. Time with validation
  const docTimes = trafficData
    .map((item) => {
      const dateUnloading = new Date(
        item.header.detail.dates_data.date_init_unloading
      );
      const dateDoda = new Date(item.header.detail.dates_data.date_doda);

      if (
        isNaN(dateUnloading.getTime()) ||
        isNaN(dateDoda.getTime()) ||
        dateDoda <= dateUnloading
      ) {
        return null; // Discard entry if either date is invalid or date_doda is not greater than date_unloading
      }

      return (dateDoda - dateUnloading) / (1000 * 60 * 60); // Convert to hours
    })
    .filter((time) => time !== null); // Filter out invalid times

  const averageDocTime =
    docTimes.length > 0
      ? (
          docTimes.reduce((acc, curr) => acc + curr, 0) / docTimes.length
        ).toFixed(2)
      : "N/A";

  const maxDocTime =
    docTimes.length > 0 ? Math.max(...docTimes).toFixed(2) : "N/A";

  const minDocTime =
    docTimes.length > 0 ? Math.min(...docTimes).toFixed(2) : "N/A";

  // Calculate Imports and Exports
  const importsCount = trafficData.filter(
    (item) => item.header.operation_type === "1"
  ).length;

  const exportsCount = trafficData.filter(
    (item) => item.header.operation_type === "2"
  ).length;

  return (
    <div className="bg-white shadow rounded-lg p-3 w-full mb-4">
      <div className="text-xs text-gray-500 text-center mb-2 italic">
        * Statistics shown for current page only ({trafficData?.length || 0}{" "}
        records)
      </div>
      <div className="grid grid-cols-6 divide-x divide-gray-200 text-center">
        <div className="flex flex-col items-center justify-center py-1">
          <FontAwesomeIcon
            icon={faList}
            className="text-blue-600 text-2xl mb-1"
          />
          <p className="text-sm text-gray-600">Unique References</p>
          <p className="text-base font-semibold text-gray-800">
            {uniqueReferencesCount}
          </p>
        </div>
        <div className="flex flex-col items-center justify-center py-1">
          <FontAwesomeIcon
            icon={faUser}
            className="text-blue-600 text-2xl mb-1"
          />
          <p className="text-sm text-gray-600">Unique Customers</p>
          <p className="text-base font-semibold text-gray-800">
            {uniqueCustomersCount}
          </p>
        </div>
        <div className="flex flex-col items-center justify-center py-1">
          <FontAwesomeIcon
            icon={faClock}
            className="text-blue-600 text-2xl mb-1"
          />
          <p className="text-sm text-gray-600">Attention Time (hrs)</p>
          <p className="text-xs text-gray-800">
            Avg: {averageAttentionTime} | Max: {maxAttentionTime} | Min:{" "}
            {minAttentionTime}
          </p>
        </div>
        <div className="flex flex-col items-center justify-center py-1">
          <FontAwesomeIcon
            icon={faFileAlt}
            className="text-green-600 text-2xl mb-1"
          />
          <p className="text-sm text-gray-600">Doc. Time (hrs)</p>
          <p className="text-xs text-gray-800">
            Avg: {averageDocTime} | Max: {maxDocTime} | Min: {minDocTime}
          </p>
        </div>
        <div className="flex flex-col items-center justify-center py-1">
          <FontAwesomeIcon
            icon={faTruckLoading}
            className="text-purple-600 text-2xl mb-1"
          />
          <p className="text-sm text-gray-600">Imports</p>
          <p className="text-base font-semibold text-gray-800">
            {importsCount}
          </p>
        </div>
        <div className="flex flex-col items-center justify-center py-1">
          <FontAwesomeIcon
            icon={faTruckMoving}
            className="text-orange-600 text-2xl mb-1"
          />
          <p className="text-xs text-gray-600">Exports</p>
          <p className="text-base font-semibold text-gray-800">
            {exportsCount}
          </p>
        </div>
      </div>
    </div>
  );
};

export default MiniDashboardTraffic;
